import React, { Suspense, useEffect } from 'react';
import logger from './utils/logger';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TokenProvider } from './contexts/TokenContext';
import CustomNavbar from './components/Navigation/NavBar';
import AdminBanner from './components/Admin/AdminBanner';
import StaleAppBanner from './components/common/StaleAppBanner';
import AdminPopup from './components/Admin/AdminPopup';
import WebsiteTrainingTour from './components/Trainings/WebsiteTrainingTour';
import FirebaseErrorBoundary from './components/FirebaseErrorBoundary';
import InactivityWarningModal from './components/common/InactivityWarningModal';
import ReportBugButton from './components/common/ReportBugButton';
import RequireAuth from './components/RequireAuth';
import {
  initializeFirebaseErrorListener,
  cleanupFirebaseErrorListener } from
  './utils/firebaseErrorListener';
import { SkeletonDashboard } from './components/common/Skeleton';
import { usePageViewTracking } from './hooks/usePageViewTracking';
import { auth } from './firebase';
import { AUTH_ERROR_CODES } from './constants/errorCodes';
import { shouldHideAppNav } from './utils/publicRoutes';

// Error boundary component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    logger.error('Error caught by boundary:', error, errorInfo);
    this.setState({ errorInfo });
  }

  componentDidUpdate(prevProps, prevState) {
    // If error state changed and it's an auth error, redirect immediately
    if (this.state.hasError && !prevState.hasError && this.state.error) {
      const error = this.state.error;
      const isLoggedOut = !auth.currentUser;
      const isAuthError = error.code && AUTH_ERROR_CODES.includes(error.code);

      if ((isAuthError || isLoggedOut) && !error.handled && !error.skipRedirect) {
        logger.warn('ErrorBoundary - Detected auth error, redirecting to login');
        window.location.replace('/login');
      }
    }
  }

  handleReset = () => {
    // Check if user is logged out before resetting
    if (!auth.currentUser) {
      logger.warn('ErrorBoundary - User logged out, redirecting to login instead of resetting');
      window.location.replace('/login');
      return;
    }
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleRefresh = () => {
    // Check if this is an auth error or user is logged out
    const error = this.state.error;
    const isLoggedOut = !auth.currentUser;
    const isAuthError = error?.code && AUTH_ERROR_CODES.includes(error.code);

    if (isAuthError || isLoggedOut) {
      logger.warn('ErrorBoundary - Auth error detected, redirecting to login instead of reloading');
      window.location.replace('/login');
    } else {
      window.location.reload();
    }
  };

  render() {
    if (this.state.hasError) {
      const error = this.state.error;
      const isLoggedOut = !auth.currentUser;
      const isAuthError = error?.code && AUTH_ERROR_CODES.includes(error.code);

      // If it's an auth error or user is logged out, redirect immediately
      if ((isAuthError || isLoggedOut) && !error?.handled && !error?.skipRedirect) {
        window.location.replace('/login');
        return null;
      }

      return (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: '100vh', padding: '20px' }}>

          <div className="text-center">
            <h2>Something went wrong.</h2>
            <p className="text-muted mt-2">
              An unexpected error occurred. You can try to recover or reload the page.
            </p>
            {process.env.NODE_ENV === 'development' && this.state.error &&
            <details className="mt-3 text-start" style={{ maxWidth: '600px', margin: '20px auto' }}>
              <summary className="text-muted small">Error details (dev only)</summary>
              <pre className="small mt-2 text-muted" style={{ overflow: 'auto', textAlign: 'left' }}>
                {this.state.error.toString()}
                {this.state.errorInfo?.componentStack}
              </pre>
            </details>
            }
            <div className="mt-4">
              <button className="btn btn-primary me-2" onClick={this.handleReset}>
                Try Again
              </button>
              <button className="btn btn-secondary" onClick={this.handleRefresh}>
                Reload Page
              </button>
            </div>
          </div>
        </div>);

    }

    return this.props.children;
  }
}

// Loading component
const LoadingFallback = () => <SkeletonDashboard />;

// App wrapper component that handles Firebase error initialization
const AppWrapper = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    currentUser,
    showInactivityWarning,
    inactivitySecondsRemaining,
    extendSession
  } = useAuth();
  const hideNavbar = !currentUser || shouldHideAppNav(location.pathname);

  // Track page views automatically
  usePageViewTracking();

  useEffect(() => {
    // Initialize Firebase error listener
    initializeFirebaseErrorListener(navigate);

    // Cleanup on unmount
    return () => {
      cleanupFirebaseErrorListener();
    };
  }, [navigate]);

  useEffect(() => {
    document.body.classList.toggle('cfsf-no-navbar', hideNavbar);
    return () => {
      document.body.classList.remove('cfsf-no-navbar');
    };
  }, [hideNavbar]);

  return (
    <FirebaseErrorBoundary>
      <a href="#main-content" className="skip-link visually-hidden-focusable">
        Skip to main content
      </a>
      {!hideNavbar && <StaleAppBanner />}
      {!hideNavbar && <AdminBanner />}
      {!hideNavbar && <CustomNavbar />}
      <Container as="main" fluid id="main-content" tabIndex={-1} className="main-container">
        <Suspense fallback={<LoadingFallback />}>
          <RequireAuth>
            <Outlet />
          </RequireAuth>
        </Suspense>
      </Container>
      <AdminPopup />
      <ReportBugButton />
      <WebsiteTrainingTour />
      <InactivityWarningModal
        show={showInactivityWarning}
        secondsRemaining={inactivitySecondsRemaining}
        onExtendSession={extendSession} />

    </FirebaseErrorBoundary>);

};

// Initialize app
const App = () => {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <TokenProvider>
          <AppWrapper />
        </TokenProvider>
      </AuthProvider>
    </ErrorBoundary>);

};

export default App;
